// Logos de las plataformas que aparecen como opciones en el CuestionarioModal.
//
// Van dentro de un chip BLANCO (ver `PlatformLogoChip`) y no sobre el fondo de
// la fila: la fila seleccionada se pinta `bg-brand-primary`, y las marcas
// full-color sobre azul quedan ilegibles (sobre todo la G de Google y el
// amarillo de MercadoLibre). Con el chip, el logo siempre se ve igual esté la
// opción marcada o no.
//
// Instagram, Facebook, Google y Empretienda son SVG inline (los tres primeros
// con sus marcas reales; Empretienda redibujada — es geometría simple y queda
// igual al original). Tiendanube reusa el `TiendaNubeIcon` que ya existía.
// MercadoLibre es el único bitmap: su apretón de manos no se puede redibujar
// decentemente a mano. Ver ADR-682.

import React from "react";
import { TiendaNubeIcon } from "./TiendaNubeIcon";

// ─── Marcas reales ───────────────────────────────────────────────────────────

const InstagramLogo: React.FC = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <rect
      x="2.5"
      y="2.5"
      width="19"
      height="19"
      rx="5.5"
      fill="none"
      stroke="#E4405F"
      strokeWidth="2"
    />
    <circle
      cx="12"
      cy="12"
      r="4.4"
      fill="none"
      stroke="#E4405F"
      strokeWidth="2"
    />
    <circle cx="17.6" cy="6.4" r="1.4" fill="#E4405F" />
  </svg>
);

const FacebookLogo: React.FC = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path
      fill="#1877F2"
      d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.09 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.09 24 18.1 24 12.07z"
    />
  </svg>
);

const GoogleLogo: React.FC = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

// ─── MercadoLibre ────────────────────────────────────────────────────────────
// Logo oficial (PNG 200x200 en `public/images`). Es el único que no es SVG
// inline: redibujar el apretón de manos a mano daba un resultado pobre —
// a este tamaño el trazo se empasta y quedaba más cerca de un bigote que de
// un apretón. Al ser un asset local no hace falta `CachedImg`.

const MercadoLibreLogo: React.FC = () => (
  <img
    src="/images/mercado-libre.png"
    alt=""
    className="w-4 h-4 object-contain"
    aria-hidden="true"
  />
);

// ─── Empretienda ─────────────────────────────────────────────────────────────
// Cuadrado verde con la bolsa blanca: marco cuadrado + "toldo" superior cuyo
// borde inferior es un arco que baja hacia el centro.

const EMPRETIENDA_VERDE = "#4CB255";

const EmpretiendaLogo: React.FC = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <rect width="24" height="24" rx="3.5" fill={EMPRETIENDA_VERDE} />
    <rect
      x="6.2"
      y="6.6"
      width="11.6"
      height="11"
      rx="1"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="1.7"
    />
    <path
      d="M6.2 7.4h11.6V8c0 2.9-2.6 5.2-5.8 5.2S6.2 10.9 6.2 8v-.6z"
      fill="#FFFFFF"
    />
  </svg>
);

// ─── VTEX ────────────────────────────────────────────────────────────────────
// Cuadrado rosa con la V blanca. El logo oficial de VTEX es el wordmark; su
// símbolo es esa V, y a 16 px una V trazada se lee mejor que una rellena (el
// vértice de abajo se empasta). Mismo tratamiento que Empretienda, que también
// es un cuadrado de color con un glifo blanco: son geometría simple y el
// resultado queda igual al original — ver ADR-682.

const VTEX_ROSA = "#F71963";

const VtexLogo: React.FC = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <rect width="24" height="24" rx="3.5" fill={VTEX_ROSA} />
    <path
      d="M7 7.4 12 16.8 17 7.4"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Resolver ────────────────────────────────────────────────────────────────

/** Devuelve el logo de una opción, o `null` si no es una marca (ej. "ninguna"). */
export function getPlatformLogo(id: string): React.ReactNode {
  switch (id) {
    case "instagram":
      return <InstagramLogo />;
    case "facebook":
      return <FacebookLogo />;
    case "google":
      return <GoogleLogo />;
    case "tiendanube":
      return <TiendaNubeIcon className="w-4 h-4 text-[#2c3357]" />;
    case "vtex":
      return <VtexLogo />;
    case "mercadolibre":
      return <MercadoLibreLogo />;
    case "empretienda":
      return <EmpretiendaLogo />;
    default:
      return null;
  }
}

/**
 * Chip blanco de tamaño fijo que envuelve al logo.
 *
 * El borde va SIN opacidad (`border-neutral-200`, no `/80`): en este proyecto los
 * tonos 100/200/400/600/700/900 de `neutral` están pisados en `tailwind.config.js`
 * con variables CSS, y Tailwind no les puede aplicar el modificador de opacidad —
 * la clase no genera regla y el borde no existía. Verificado contra `dist/*.css`. Cuando la opción no tiene
 * marca (ej. "No pagamos por publicidad") se renderiza igual pero vacío e
 * invisible, para que todas las etiquetas de la lista queden alineadas.
 */
export const PlatformLogoChip: React.FC<{ id: string }> = ({ id }) => {
  const logo = getPlatformLogo(id);
  if (!logo) return <span className="w-7 h-7 shrink-0" aria-hidden="true" />;
  return (
    <span className="w-7 h-7 rounded-[8px] bg-white border border-neutral-200 inline-flex items-center justify-center shrink-0 overflow-hidden">
      {logo}
    </span>
  );
};

export default PlatformLogoChip;
